import { useTranslation } from "react-i18next";

import { Box, Flex, Spacer } from "@chakra-ui/react";

import { ClassesEnum } from "@ts/enums/ClassesEnum";

import { getTrackingId } from "@utils/utils";

import { Button } from "@components/Button";

export interface PropTypes {
    hasBackButton?: boolean;
    hasNextButton?: boolean;
    hasSkipButton?: boolean;
    hasCancelButton?: boolean;
    isNextBtnLoading?: boolean;
    backBtnText?: React.ReactNode;
    nextBtnText?: React.ReactNode;
    cancelBtnText?: React.ReactNode;
    canGoNext?: boolean;
    canGoBack?: boolean;
    btnClassHierarchy?: {
        next?: ClassesEnum[];
        back?: ClassesEnum[];
        skip?: ClassesEnum[];
    };
    btnActions?: {
        next?: string;
        back?: string;
        skip?: string;
    };
    onGoNext?: () => void;
    onGoBack?: () => void;
    onCancel?: () => void;
    isNextBtnDisabled?: boolean;
}

const FooterActions = ({
    hasBackButton = true,
    hasNextButton = true,
    hasSkipButton = false,
    hasCancelButton = false,
    isNextBtnLoading = false,
    backBtnText,
    nextBtnText,
    cancelBtnText,
    canGoNext = true,
    canGoBack = true,
    btnClassHierarchy,
    btnActions,
    isNextBtnDisabled = false,
    onGoNext,
    onGoBack,
    onCancel
}: PropTypes) => {
    const { t } = useTranslation();

    return (
        <Flex
            display={{ base: "block", md: "flex" }}
            // maxW="1280px"
            justify="space-between"
        >
            <Box w={{ base: "100%", md: "200px" }} marginBottom="15px">
                {hasBackButton && (
                    <Button
                        className={ClassesEnum.Button}
                        data-tracking-id={getTrackingId(
                            btnClassHierarchy?.back ?? [ClassesEnum.Button],
                            btnActions?.back ?? "goBack"
                        )}
                        data-testid="go-back-btn"
                        onClick={onGoBack}
                        isDisabled={!canGoBack}
                        colorScheme="ebonyClay"
                        variant="outline"
                        mr="auto"
                        w="100%"
                    >
                        {backBtnText ?? "Go Back"}
                    </Button>
                )}
                {hasCancelButton && !hasBackButton && (
                    <Button
                        className={ClassesEnum.Button}
                        data-tracking-id={getTrackingId(
                            btnClassHierarchy?.back ?? [ClassesEnum.Button],
                            btnActions?.back ?? "cancel"
                        )}
                        data-testid="cancel-btn"
                        onClick={onCancel}
                        colorScheme="ebonyClay"
                        variant="outline"
                        mr="auto"
                        w="100%"
                    >
                        {cancelBtnText ?? "Cancel"}
                    </Button>
                )}
            </Box>
            <Spacer />
            <Flex w={{ base: "100%", md: "200px" }} gap={2} justify="flex-end">
                {hasSkipButton && (
                    <Button
                        className={ClassesEnum.Button}
                        data-tracking-id={getTrackingId(
                            btnClassHierarchy?.skip ?? [ClassesEnum.Button],
                            btnActions?.skip ?? "skip"
                        )}
                        data-testid="go-skip-btn"
                        onClick={onGoNext}
                        colorScheme="ebonyClay"
                        variant="outline"
                    >
                        {t("footer.SkipBtn", "Skip this")}
                    </Button>
                )}
                {hasNextButton && (
                    <Button
                        className={ClassesEnum.Button}
                        data-tracking-id={getTrackingId(
                            btnClassHierarchy?.next ?? [ClassesEnum.Button],
                            btnActions?.next ?? "next"
                        )}
                        data-testid="go-next-btn"
                        isLoading={isNextBtnLoading}
                        onClick={onGoNext}
                        colorScheme="ebonyClay"
                        isDisabled={!canGoNext || isNextBtnDisabled}
                        flex="1"
                        w="100%"
                    >
                        {nextBtnText ?? t("footer.nextBtn", "Next")}
                    </Button>
                )}
            </Flex>
        </Flex>
    );
};

export default FooterActions;
